import React, {useCallback, useEffect, useMemo} from 'react';

import {yupResolver} from '@hookform/resolvers/yup';

import {useUnit} from 'effector-react';
import {Controller, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';

import * as yup from 'yup';

import {ROUTES} from '@app/routing';
import {colors} from '@app/styles/theme';

import {passwordModel} from '@entities/password/model';
import {userModel} from '@entities/user/model';
import {history} from '@shared/lib/history';
import {Button, ButtonVariant} from '@shared/ui/Button';
import {Input} from '@shared/ui/Input';

import {getPasswordSchema} from './helpers/passwordSchema';
import {PasswordValidationRules} from './helpers/passwordValidationRules';

import {
    ButtonContainer,
    Field,
    Fields,
    Form,
    FormDescription,
    MainContainer,
    PasswordContent,
    PasswordTitle,
} from './styled';

type ChangePasswordForm = {currentPassword: string; password: string; passwordConfirm: string};

export function ChangePasswordForm() {
    const {t, i18n} = useTranslation();
    const password = useUnit(passwordModel);
    const user = useUnit(userModel);

    const translateValidates = useCallback(
        (key: string, replace?: Record<string, any>) => t(`password.validations.${key}`, replace),
        [i18n.language],
    );

    const schema = useMemo(
        () =>
            yup.object().shape({
                currentPassword: yup.string().required(translateValidates('required')),
                password: getPasswordSchema(translateValidates),
                passwordConfirm: yup
                    .string()
                    .required(translateValidates('required'))
                    .oneOf([yup.ref('password')], translateValidates('confirm_password_mismatch')),
            }),
        [translateValidates],
    );

    const {control, handleSubmit, formState, watch, errors} = useForm<ChangePasswordForm>({
        resolver: yupResolver(schema),
        mode: 'all',
        defaultValues: {
            currentPassword: '',
            password: '',
            passwordConfirm: '',
        },
    });

    const watchPassword = watch('password', '');

    useEffect(() => {
        if (password.state.success) history.push(ROUTES.LOGIN);
    }, [password.state.success]);

    const onSubmit = async (data: ChangePasswordForm) => {
        if (!user.state.profile?.email) return;
        try {
            await password.change({
                currentPassword: data.currentPassword,
                newPassword: data.password,
                confirmation: data.passwordConfirm,
                userName: user.state.profile.email,
                email: user.state.profile.email,
            });
            user.logout(true);
        } catch (e) {
            console.error(e);
        }
    };

    return (
        <MainContainer>
            <PasswordContent style={{background: colors.background, padding: 0}}>
                <PasswordTitle size={3}>{t('password.change.title')}</PasswordTitle>
                <Form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
                    <FormDescription>{t('password.change.description')}</FormDescription>
                    <Fields>
                        {/* Current Password */}
                        <Field
                            isError={!!errors.currentPassword}
                            text={errors.currentPassword?.message}
                        >
                            <span>{t('password.change.fields.currentPassword.label')}</span>
                            <Controller
                                name="currentPassword"
                                control={control}
                                render={({onChange, value, name}) => (
                                    <Input
                                        name={name}
                                        value={value}
                                        onChange={onChange}
                                        type="password"
                                        placeholder={t(
                                            'password.change.fields.currentPassword.placeholder',
                                        )}
                                        disabled={password.state.pending}
                                        hasError={!!errors.currentPassword}
                                    />
                                )}
                            />
                        </Field>

                        {/* New Password */}
                        <Field isError={!!errors.password}>
                            <span>{t('password.change.fields.newPassword.label')}</span>
                            <Controller
                                name="password"
                                control={control}
                                render={({onChange, value, name}) => (
                                    <Input
                                        name={name}
                                        value={value}
                                        onChange={onChange}
                                        type="password"
                                        placeholder={t(
                                            'password.change.fields.newPassword.placeholder',
                                        )}
                                        autoComplete="new-password"
                                        disabled={password.state.pending}
                                        hasError={!!errors.password}
                                        isValid={formState.dirtyFields.password && !errors.password}
                                    />
                                )}
                            />
                            <PasswordValidationRules watchValue={watchPassword} />
                        </Field>

                        {/* Confirm New Password */}
                        <Field
                            isError={!!errors.passwordConfirm}
                            text={errors.passwordConfirm?.message}
                        >
                            <span>{t('password.change.fields.confirmNewPassword.label')}</span>
                            <Controller
                                name="passwordConfirm"
                                control={control}
                                render={({onChange, value, name}) => (
                                    <Input
                                        name={name}
                                        value={value}
                                        onChange={onChange}
                                        type="password"
                                        placeholder={t(
                                            'password.change.fields.confirmNewPassword.placeholder',
                                        )}
                                        autoComplete="new-password"
                                        disabled={password.state.pending}
                                        hasError={!!errors.passwordConfirm}
                                        isValid={
                                            formState.dirtyFields.passwordConfirm &&
                                            !errors.passwordConfirm
                                        }
                                    />
                                )}
                            />
                        </Field>
                    </Fields>

                    <ButtonContainer>
                        <Button
                            type="submit"
                            isLoading={password.state.pending}
                            disabled={!formState.isValid}
                            data-id="btn-submit"
                        >
                            {t('password.change.submitButton')}
                        </Button>
                        <Button
                            variant={ButtonVariant.Text}
                            onClick={() => history.push(ROUTES.PROFILE)}
                        >
                            {t('password.change.returnButton')}
                        </Button>
                    </ButtonContainer>
                </Form>
            </PasswordContent>
        </MainContainer>
    );
}
